/**
 * Tab Location Context
 *
 * Provides a per-tab Location object to components rendered inside KeepAliveOutlet.
 * This is necessary because KeepAliveOutlet keeps all tab components mounted (CSS-hidden),
 * so react-router-dom's useLocation() returns the *active* tab's URL — not the
 * background tab's own URL. Components that derive state from location.search
 * (e.g. ?tab=card) would reset when another tab is focused.
 *
 * Pattern mirrors RouteParamsContext + @/hooks/useParams.
 */

import React, { createContext, useContext, useMemo } from 'react';

export type TabLocation = {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
  paneId?: string;
};

const TabLocationContext = createContext<TabLocation | null>(null);

export const TabLocationProvider: React.FC<{
  pathname: string;
  search: string;
  state?: unknown;
  tabKey: string;
  paneId?: string;
  children: React.ReactNode;
}> = ({ pathname, search, state, tabKey, paneId, children }) => {
  const location = useMemo<TabLocation>(
    () => ({ pathname, search, hash: '', state: state ?? null, key: tabKey, paneId }),
    [pathname, search, state, tabKey, paneId],
  );

  return (
    <TabLocationContext.Provider value={location}>
      {children}
    </TabLocationContext.Provider>
  );
};

/**
 * Returns the tab-specific location when inside KeepAliveOutlet, or null outside.
 */
export const useTabLocation = (): TabLocation | null => useContext(TabLocationContext);

export default TabLocationContext;
